import { notify } from '@billy/design-system'
import { createContext, ReactElement, ReactNode, useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SkatIntegrationStatus } from '@features/vatDeclarations/enums/skatIntegrationStatus'
import { useVatDeclaration as fetchDeclarationData } from '@features/vatDeclarations/hooks/useVatDeclaration'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { NotificationKeys } from '../../../../../../../enums/notificationKeys'
import { useLocale } from '../../../../../../../hooks/useLocale'
import { useSubmitDeclarationToSkat } from '../../../../../hooks/useSubmitDeclarationToSkat'
import { useTrackVatDeclarationEvent } from '../../../../../hooks/useTrackVatDeclarationEvent'

interface SubmitToSkatContextState {
  isAcceptedBySkat: boolean
  connectionState: SkatIntegrationStatus
  setConnectionState: (state: SkatIntegrationStatus) => void
  handleSkatHandshake: () => void
}

const SubmitToSkatContext = createContext<SubmitToSkatContextState | undefined>(undefined)

interface SubmitToSkatContextProps {
  children?: ReactNode
  declarationId: string
  isSubmissionFormOpen: boolean
}

export const SubmitToSkatContextProvider = ({
  children,
  declarationId,
  isSubmissionFormOpen,
}: SubmitToSkatContextProps): ReactElement | null => {
  const { t } = useTranslation()
  const { code } = useLocale()

  const { organization } = useUserOrganization()
  const [connectionState, setConnectionState] = useState(SkatIntegrationStatus.Idle)
  const trackEvent = useTrackVatDeclarationEvent()

  const { vatDeclaration } = fetchDeclarationData({
    vatDeclarationId: declarationId,
    pollingEnabled: connectionState === SkatIntegrationStatus.Connecting,
  })
  const { isSettled } = vatDeclaration || {}

  const { submitDeclarationToSkat } = useSubmitDeclarationToSkat({
    onSuccess: (response) => {
      if (response?.deep_link) {
        window.open(response?.deep_link)
        trackEvent('xxx - Bookkeeping - VAT - Submit VAT DK', {
          flow: 'Success',
          step: '2 - Redirected',
        })

        setConnectionState(SkatIntegrationStatus.Connecting)

        trackEvent('xxx - Bookkeeping - VAT - Submit VAT DK', {
          flow: 'Success',
          step: '3 - Connecting',
        })
      } else {
        notify({
          id: NotificationKeys.VatDeclarationsSettle,
          message: t('skat.submit_form.error'),
          variant: 'error',
        })

        trackEvent('xxx - Bookkeeping - VAT - Submit VAT DK', {
          flow: 'Failed',
          step: '2 - Redirected',
          reason: 'No redirect link provided',
        })

        setConnectionState(SkatIntegrationStatus.Idle)
      }
    },
    onError: (e) => {
      const message = (code === 'da' && e?.error?.dk) || e?.error?.en || t('skat.submit_form.error')
      setConnectionState(SkatIntegrationStatus.Idle)
      notify({
        id: NotificationKeys.VatDeclarationsSettle,
        message,
        variant: 'error',
      })
      trackEvent('xxx - Bookkeeping - VAT - Submit VAT DK', {
        flow: 'Failed',
        step: '2 - Redirected',
        reason: message,
      })
    },
  })

  const handleSkatHandshake = useCallback(() => {
    if (!organization) {
      return
    }
    setConnectionState(SkatIntegrationStatus.Redirecting)

    setTimeout(() => {
      submitDeclarationToSkat({
        organizationId: organization?.id,
        declarationId,
        registrationNumber: organization.registrationNo,
      })
    }, 1000)
  }, [declarationId, organization, submitDeclarationToSkat])

  useEffect(() => {
    /* Stop polling when we receive info that the declaration is settled */
    if (isSubmissionFormOpen && isSettled) {
      setConnectionState(SkatIntegrationStatus.Idle)

      trackEvent('xxx - Bookkeeping - VAT - Submit VAT DK', {
        flow: 'Success',
        step: '4 - Success',
      })
    }
  }, [isSubmissionFormOpen, isSettled, trackEvent])

  return (
    <SubmitToSkatContext.Provider
      value={{
        connectionState,
        setConnectionState,
        isAcceptedBySkat: !!isSettled,
        handleSkatHandshake,
      }}
    >
      {children}
    </SubmitToSkatContext.Provider>
  )
}

export const useSubmitToSkat = () => {
  const context = useContext(SubmitToSkatContext)

  if (!context) {
    throw new Error('SubmitToSkatContextProvider is missing in the module!')
  }

  return context
}
